'use client';

import { useState, useMemo, useEffect } from 'react';
import BlogCard from './BlogCard';
import { BlogPostMeta } from '@/lib/blog';

interface BlogFilterProps {
  posts: BlogPostMeta[];
}

type ActiveFilter =
  | { type: 'all' }
  | { type: 'category'; slug: string; name: string }
  | { type: 'tag'; value: string };

export default function BlogFilter({ posts }: BlogFilterProps) {
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>({ type: 'all' });
  const [filterPage, setFilterPage] = useState(0);

  const categories = useMemo(() => {
    const seen = new Set<string>();
    const result: { id: number; name: string; slug: string }[] = [];
    for (const post of posts) {
      for (const cat of post.categories ?? []) {
        if (!seen.has(cat.slug)) {
          seen.add(cat.slug);
          result.push(cat);
        }
      }
    }
    return result.sort((a, b) => a.name.localeCompare(b.name));
  }, [posts]);

  const tags = useMemo(() => {
    const seen = new Set<string>();
    for (const post of posts) {
      for (const tag of post.tags ?? []) seen.add(tag);
    }
    return Array.from(seen).sort((a, b) => a.localeCompare(b));
  }, [posts]);

  const hasFilters = categories.length > 0 || tags.length > 0;

  const filterItems = useMemo(
    () => [
      { key: 'all', type: 'all' as const, label: 'All' },
      ...categories.map((cat) => ({
        key: `cat-${cat.slug}`,
        type: 'category' as const,
        label: cat.name,
        slug: cat.slug,
      })),
      ...tags.map((tag) => ({
        key: `tag-${tag}`,
        type: 'tag' as const,
        label: `#${tag}`,
        value: tag,
      })),
    ],
    [categories, tags]
  );

  const itemsPerPage = 10;
  const totalPages = Math.max(1, Math.ceil(filterItems.length / itemsPerPage));
  const visibleFilterItems = filterItems.slice(
    filterPage * itemsPerPage,
    filterPage * itemsPerPage + itemsPerPage
  );

  useEffect(() => {
    setFilterPage((currentPage) => Math.min(currentPage, totalPages - 1));
  }, [totalPages]);

  const goToPage = (nextPage: number) => {
    const boundedPage = Math.max(0, Math.min(nextPage, totalPages - 1));
    setFilterPage(boundedPage);
  };

  const activateFilter = (item: (typeof filterItems)[number]) => {
    if (item.type === 'all') {
      setActiveFilter({ type: 'all' });
      setFilterPage(0);
      return;
    }

    if (item.type === 'category') {
      setActiveFilter({ type: 'category', slug: item.slug, name: item.label });
    } else {
      setActiveFilter({ type: 'tag', value: item.value });
    }

    const index = filterItems.findIndex((filterItem) => filterItem.key === item.key);
    if (index >= 0) setFilterPage(Math.floor(index / itemsPerPage));
  };

  const filteredPosts = useMemo(() => {
    if (activeFilter.type === 'all') return posts;
    if (activeFilter.type === 'category')
      return posts.filter((p) =>
        (p.categories ?? []).some((c) => c.slug === activeFilter.slug)
      );
    return posts.filter((p) => (p.tags ?? []).includes(activeFilter.value));
  }, [posts, activeFilter]);

  const gridHeading =
    activeFilter.type === 'category'
      ? activeFilter.name
      : activeFilter.type === 'tag'
      ? `#${activeFilter.value}`
      : 'All Articles';

  return (
    <div>
      {/* Filter Section */}
      {hasFilters && (
        <section className="mx-auto max-w-7xl px-6 pt-10 pb-2">
          <div className="mb-5 flex items-center gap-3">
            <div className="h-1 w-12 bg-[#FFC933]" />
            <h2 className="text-2xl font-bold text-[#2B0A4A]">Browse Articles</h2>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => goToPage(filterPage - 1)}
              disabled={filterPage === 0}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-[#E8DBD0] bg-white text-[#2B0A4A] shadow-sm transition-all hover:bg-[#2B0A4A] hover:text-[#FFF8EC] disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Previous filters"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <div className="flex flex-1 flex-wrap gap-2">
              {visibleFilterItems.map((item) => {
                const isActive =
                  (item.type === 'all' && activeFilter.type === 'all') ||
                  (item.type === 'category' &&
                    activeFilter.type === 'category' &&
                    activeFilter.slug === item.slug) ||
                  (item.type === 'tag' &&
                    activeFilter.type === 'tag' &&
                    activeFilter.value === item.value);

                return (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => activateFilter(item)}
                    className={`inline-flex w-auto shrink-0 whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-semibold transition-all ${
                      isActive
                        ? item.type === 'all'
                          ? 'bg-[#2B0A4A] text-white shadow-md'
                          : item.type === 'category'
                          ? 'bg-[#FFC933] text-[#2B0A4A] shadow-md'
                          : 'bg-[#FF8A3D] text-white shadow-md'
                        : 'border border-[#E8DBD0] bg-white text-[#5B4C63] hover:border-[#2B0A4A] hover:text-[#2B0A4A]'
                    }`}
                  >
                    {item.label}
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              onClick={() => goToPage(filterPage + 1)}
              disabled={filterPage >= totalPages - 1}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-[#E8DBD0] bg-white text-[#2B0A4A] shadow-sm transition-all hover:bg-[#2B0A4A] hover:text-[#FFF8EC] disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Next filters"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </section>
      )}

      {/* Posts Grid */}
      <section className="mx-auto max-w-7xl px-6 py-10">
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-1 w-12 bg-[#FFC933]" />
            <h2 className="text-2xl font-bold text-[#2B0A4A]">{gridHeading}</h2>
          </div>
          <span className="text-sm text-gray-500">
            {filteredPosts.length} {filteredPosts.length === 1 ? 'article' : 'articles'}
          </span>
        </div>

        {filteredPosts.length > 0 ? (
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {filteredPosts.map((post) => (
              <BlogCard key={post.slug} post={post} />
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border-2 border-dashed border-[#E8DBD0] bg-white p-12 text-center">
            <svg
              className="mx-auto mb-4 h-16 w-16 text-[#B9A8C1]"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <h3 className="mb-2 text-xl font-semibold text-[#2B0A4A]">
              {posts.length === 0 ? 'No blog posts yet' : 'No articles match this filter'}
            </h3>
            <p className="text-[#5B4C63]">
              {posts.length === 0 ? 'Check back soon for exciting content!' : 'Try selecting a different filter.'}
            </p>
          </div>
        )}
      </section>
    </div>
  );
}
